/* eslint-disable react/destructuring-assignment */
import { Badge, Divider, Grid, Group, Paper, SimpleGrid, Space, Text, ThemeIcon } from '@mantine/core';
import { IconCoin, IconScale, IconZoomMoney } from '@tabler/icons-react';
import { Tabs } from 'antd';
import PageViewAudit from '../../../../../../../components/core/PageViewAudit/PageViewAudit';
import PageViewProperty from '../../../../../../../components/core/PageViewProperty/PageViewProperty';
import ProfileCardLink from '../../../../../../../components/core/ProfileCardLink/ProfileCardLink';
import { EventVehicleQuotationType } from '../../../../../../../models/core/events.type';
import { Feature } from '../../../../../../../utils/constants.utils';
import { formatCurrency } from '../../../../../../../utils/formatter.utils';
import { isNullOrEmpty } from '../../../../../../../utils/helper.utils';
import ProposalItemToleranceView from '../../../../../proposals/ProposalView/components/components/components/ProposalItemToleranceView';

type DataViewProps = {
  data: EventVehicleQuotationType;
  compra: boolean;
};

export default function EventVehicleQuotation(props: DataViewProps) {
  const customerPricing = props.data.precificacao;
  const customerQuantityLabel = isNullOrEmpty(customerPricing?.quantidade)
    ? '-'
    : `${customerPricing?.quantidade?.toString() || '-'} ${
        customerPricing?.quantidadeUnidadeMedida || '-'
      } / ${customerPricing?.frequenciaUnidadeMedida || '-'}`;

  const supplierQuantityLabel = isNullOrEmpty(props.data.quantidade)
    ? '-'
    : `${props.data.quantidade?.toString() || '-'} ${props.data.quantidadeUnidadeMedida || '-'} / ${
        props.data.frequenciaUnidadeMedida || '-'
      }`;

  const buildTabs = () => [
    {
      key: 'Cotacao',
      label: (
        <Group>
          <ThemeIcon color={Feature.Home.Event.color} variant="outline">
            <IconZoomMoney size={18} />
          </ThemeIcon>
          <Text size="md" color={Feature.Home.Event.color} style={{ fontWeight: 500 }}>
            Cotação
          </Text>
        </Group>
      ),
      children: (
        <div>
          <Paper shadow="xs" p="md" withBorder>
            <Grid columns={9}>
              <Grid.Col span={3}>
                <PageViewProperty
                  label="Fornecedor"
                  text={
                    props.data.fornecedorCNPJ ? (
                      <ProfileCardLink
                        id={props.data.idFornecedor?.toString() || '???'}
                        name={props.data.fornecedorNomeFantasia || props.data.fornecedorRazaoSocial || '-'}
                        nameSize="sm"
                        description={
                          props.data.fornecedorNomeFantasia
                            ? props.data.fornecedorRazaoSocial
                            : props.data.fornecedorCNPJ
                        }
                        descriptionSize="xs"
                        linkPrefix="entities"
                        showLink
                      />
                    ) : (
                      <ProfileCardLink
                        id={props.data.idFornecedor?.toString() || '???'}
                        name={props.data.fornecedorNome || '-'}
                        nameSize="sm"
                        description={props.data.fornecedorCPF}
                        descriptionSize="xs"
                        linkPrefix="entities"
                        showLink
                      />
                    )
                  }
                  size="sm"
                />
              </Grid.Col>
              <Grid.Col span={2}>
                <PageViewProperty
                  label="Cobrar se Compartilhado?"
                  text={props.data.cobrarSeCompartilhado ? 'Sim' : 'Não'}
                  size="sm"
                />
              </Grid.Col>
              <Grid.Col span={2}>
                <PageViewProperty label="Quantidade/Frequência" text={supplierQuantityLabel} size="sm" />
              </Grid.Col>
              <Grid.Col span={1}>
                <PageViewProperty
                  label="Mínimo Aceitável"
                  text={
                    !props.data.minimoAceitavel
                      ? '-'
                      : `${props.data.minimoAceitavel} ${props.data.minimoAceitavelUnidadeMedida || '-'}`
                  }
                  size="sm"
                />
              </Grid.Col>
              <Grid.Col span={1}>
                <PageViewProperty
                  label="Preço do Fornecedor"
                  text={isNullOrEmpty(props.data.preco) ? '-' : formatCurrency(props.data.preco)}
                  size="sm"
                />
              </Grid.Col>
            </Grid>
            <Divider my="sm" variant="dotted" />

            <Grid columns={4}>
              <Grid.Col span={2}>
                <PageViewProperty
                  label="Quantidade/Frequência do Cliente"
                  text={customerQuantityLabel}
                  size="sm"
                />
              </Grid.Col>
              <Grid.Col span={1}>
                <PageViewProperty
                  label="Preço do Cliente"
                  text={isNullOrEmpty(customerPricing?.preco) ? '-' : formatCurrency(customerPricing!.preco)}
                  size="sm"
                />
              </Grid.Col>
              <Grid.Col span={1}>
                <PageViewProperty
                  label="Imposto do Cliente"
                  text={isNullOrEmpty(customerPricing?.imposto) ? '-' : `${customerPricing!.imposto}%`}
                  size="sm"
                />
              </Grid.Col>
            </Grid>
            <Divider my="sm" variant="dotted" />

            <SimpleGrid cols={1}>
              <PageViewProperty label="Observação" text={props.data.observacao || '-'} size="sm" />
            </SimpleGrid>
          </Paper>
          <Space h="lg" />

          <PageViewAudit
            idCriadoPor={props.data.idCriadoPor}
            criadoPor={props.data.criadoPor}
            dataCriacao={props.data.dataCriacao}
            idModificadoPor={props.data?.idModificadoPor || null}
            modificadoPor={props.data?.modificadoPor || null}
            dataModificacao={props.data?.dataModificacao || null}
            size="sm"
            gridSize={2}
          />
        </div>
      ),
      forceRender: true,
    },
    {
      key: 'Cliente',
      label: (
        <Group>
          <ThemeIcon color={Feature.Home.Event.color} variant="outline">
            <IconCoin size={18} />
          </ThemeIcon>
          <Text size="md" color={Feature.Home.Event.color} style={{ fontWeight: 500 }}>
            Cliente
          </Text>
        </Group>
      ),
      children: (
        <Paper shadow="xs" p="md" withBorder>
          <SimpleGrid cols={3}>
            <PageViewProperty label="Base" text={customerQuantityLabel} size="sm" />
            <PageViewProperty
              label="Preço"
              text={isNullOrEmpty(customerPricing?.preco) ? '-' : formatCurrency(customerPricing!.preco)}
              size="sm"
            />
            <PageViewProperty
              label="Imposto"
              text={isNullOrEmpty(customerPricing?.imposto) ? '-' : `${customerPricing!.imposto}%`}
              size="sm"
            />
          </SimpleGrid>
          <Space h="md" />
          <Group>
            <Badge color={props.compra ? 'red' : 'green'} variant="light">
              {props.compra ? 'Compra' : 'Venda'}
            </Badge>
          </Group>
        </Paper>
      ),
      forceRender: true,
    },
    {
      key: 'Tolerancias',
      label: (
        <Group>
          <ThemeIcon color={Feature.Home.Event.color} variant="outline">
            <IconScale size={18} />
          </ThemeIcon>
          <Text size="md" color={Feature.Home.Event.color} style={{ fontWeight: 500 }}>
            Tolerâncias
          </Text>
        </Group>
      ),
      children: (
        <Paper shadow="xs" p="md" withBorder>
          <ProposalItemToleranceView data={props.data.tolerancias} />
        </Paper>
      ),
      forceRender: true,
    },
  ];

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Tabs items={buildTabs()} defaultActiveKey="Cotacao" />
    </Paper>
  );
}
